import React from 'react';
import {
  Dimensions,
  Platform,
  SectionListData,
  StyleSheet,
  View,
} from 'react-native';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {AppText} from '../../components/AppText';
import {Button} from '../../components/Button';
import {HeatProfileCard} from '../../components/HeatProfileCard';
import {SectionTitle} from '../../components/SectionTitle';
import {
  SwappableSectionList,
  SwappableSectionListProps,
  SwappableSectionListRenderItem,
} from '../../components/SwappableSectionList';
import {SwitchableBackground} from '../../components/SwitchableBackground';
import {Constants} from '../../constants';
import {Alerts, Messages, Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {PlusIcon} from '../../icons';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useChamberType} from '../../lib/hooks/useChamberType';
import {useLoadAccountHeatProfiles} from '../../lib/hooks/useLoadAccountHeatProfiles';
import {useTheme} from '../../lib/hooks/useTheme';
import {
  connectedPeakSelector,
  currentDeviceHeatProfilesSelector,
} from '../../lib/redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {accountHeatProfilesSelector} from '../../lib/redux/slices/profilesSlice';
import {userSelector} from '../../lib/redux/slices/userSlice';
import {HeatProfile, getHeatProfileMoodLight} from '../../lib/types';
import {maxProfileGuard} from '../../lib/utils/maxProfileGuard';
import {isLegacyUser, verifyGuard} from '../../lib/utils/userFunctions';
import type {HomeTabNavigatorScreenProps} from '../../navigators/HomeTabNavigator';
import {toHeatProfileCreate, toHeatProfileList} from '../../navigators/util';
import {guardianTheme} from '../../themes';

export interface Props {}

interface ScreenProps
  extends HomeTabNavigatorScreenProps<typeof Screens.HeatProfileList> {}

export const HeatProfileListScreen: React.FC<ScreenProps> = ({navigation}) => {
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);
  const isWeb = Platform.OS === 'web';
  const insets = useAdaptiveSafeArea();

  const theme = useTheme();
  const {
    navMenuIconColor,
    navMenuTitleStyle,
    primaryTextColor,
    heatProfileListScreenTheme: {background, hoverBackgroundColor},
    tabBarTheme,
  } = theme;

  // Redux
  const user = useSelector(userSelector);
  const accountHeatProfiles = useSelector(accountHeatProfilesSelector);
  const deviceHeatProfiles = useSelector(currentDeviceHeatProfilesSelector);
  const peak = useSelector(connectedPeakSelector);

  const {isVaporEnabled} = useChamberType();

  useLoadAccountHeatProfiles();

  const withDisconnectedAlert = React.useCallback(
    (cb: () => void) => {
      if (!peak) {
        Alert.alert(Alerts.NOT_CONNECTED, Alerts.CONNECT_TO_DEVICE);
        return;
      }

      cb();
    },
    [peak],
  );

  const onPressAdd = React.useCallback(() => {
    if (user) {
      verifyGuard(
        user,
        () => {
          withDisconnectedAlert(() =>
            navigation.navigate(...toHeatProfileCreate().value),
          );
        },
        navigation.navigate,
        user.defaultUsername || isLegacyUser(user), // legacy user can create
      );
    } else {
      navigation.navigate(Screens.Login, {
        redirect: toHeatProfileList.encode(),
      });
    }
  }, [user, navigation, withDisconnectedAlert]);

  const onAddButtonPress = React.useCallback(() => {
    if (!onPressAdd || !accountHeatProfiles) return;

    maxProfileGuard(accountHeatProfiles.length, onPressAdd);
  }, [onPressAdd, accountHeatProfiles]);

  React.useEffect(() => {
    navigation.setOptions({
      headerTitle: 'HEAT PROFILES',
      headerTitleStyle: navMenuTitleStyle,
      headerRight: () => (
        <Button variant="transparent" onPress={onAddButtonPress}>
          <PlusIcon color={navMenuIconColor} />
        </Button>
      ),
    });
  }, [navMenuIconColor, navMenuTitleStyle, navigation, onAddButtonPress]);

  const renderItem: SwappableSectionListRenderItem<HeatProfile> =
    React.useCallback(
      ({info, startDrag, endDrag}) => {
        const {item: profile} = info;

        const moodLight = getHeatProfileMoodLight(moodLights)(profile);

        return (
          <HeatProfileCard
            moodLight={moodLight}
            theme={theme}
            profile={profile}
            editMode={false}
            onLongPress={startDrag}
            onPressOut={endDrag}
            isVaporEnabled={isVaporEnabled}
            onPress={() => {
              withDisconnectedAlert(() =>
                navigation.navigate(Screens.HeatProfileSelect, {
                  profileId: profile.id,
                }),
              );
            }}
          />
        );
      },
      [withDisconnectedAlert, moodLights, navigation, theme, isVaporEnabled],
    );

  const onChange: SwappableSectionListProps<HeatProfile>['onChange'] = () => {
    // TODO: Check if this needs to be implemented before redesign?
  };

  const sections = React.useMemo(
    () => [
      {
        key: 'device',
        data: [...deviceHeatProfiles].sort((a, b) => a.order - b.order),
      },
      {
        key: `account`,
        data: [...accountHeatProfiles]
          .filter(p => !deviceHeatProfiles.some(dp => dp.id === p.id))
          .sort((a, b) => a.order - b.order),
      },
    ],
    [deviceHeatProfiles, accountHeatProfiles],
  );

  if (!deviceHeatProfiles || !accountHeatProfiles) return null;

  const getSectionTitle = (section: SectionListData<HeatProfile, unknown>) => {
    switch (section.key) {
      case 'device':
        return Strings.heatProfiles.sections.device;
      case 'account':
        return `${Strings.heatProfiles.sections.account} ${section.data.length}/${Constants.MAX_HEAT_PROFILES}`;
      default:
        return '';
    }
  };

  return (
    <SwitchableBackground {...{background}}>
      <View
        style={{
          flex: 1,
          height: isWeb ? Dimensions.get('screen').height : '100%',
          paddingTop: insets.top,
          paddingBottom:
            insets.bottom +
            tabBarTheme.height -
            (isWeb || theme !== guardianTheme ? 0 : 4),
        }}>
        <View style={styles.screen}>
          <View style={styles.container}>
            <SwappableSectionList
              editingList={false}
              {...{sections, renderItem}}
              contentContainerStyle={
                theme !== guardianTheme && {paddingBottom: 4}
              }
              renderSectionHeader={item => (
                <SectionTitle
                  titleStyle={{
                    fontFamily: 'Roboto-Regular',
                    fontWeight: '400',
                    letterSpacing: 0.05,
                  }}
                  title={getSectionTitle(item.section)}
                />
              )}
              insertionMapping={[
                {from: 'account', to: 'account'},
                {from: 'device', to: 'account'},
              ]}
              keyExtractor={item => item.id}
              onChange={onChange}
              stickySectionHeadersEnabled={false}
              hoverBackgroundColor={hoverBackgroundColor}
            />
          </View>

          {!user && (
            <View style={styles.textsContainer}>
              <AppText
                style={[
                  styles.noticeText,
                  {
                    color: primaryTextColor,
                  },
                ]}>
                {Messages.HEAT_PROFILE_NOTICE}
              </AppText>

              <AppText
                onPress={() => {
                  navigation.navigate(Screens.Login, {
                    redirect: toHeatProfileList.encode(),
                  });
                }}
                style={[
                  styles.loginText,
                  {
                    color: primaryTextColor,
                  },
                ]}>
                Log in
              </AppText>
            </View>
          )}
        </View>
      </View>
    </SwitchableBackground>
  );
};

const styles = StyleSheet.create({
  screen: {
    alignItems: 'center',
    paddingTop: 70,
    flex: 1,
  },
  container: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 10,
  },
  textsContainer: {
    width: '100%',
    paddingVertical: 12,
  },
  noticeText: {textAlign: 'center', paddingHorizontal: 40},
  loginText: {
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
});
